import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useProfile } from "@/hooks/useProfile";
import { FeedbackButton } from "./FeedbackButton";
import { NotificationBell } from "./NotificationBell";
import { GibletChatButton } from "./chat/GibletChatButton";
import { GibletChatDialogEnhanced } from "./chat/GibletChatDialogEnhanced";
import { NavbarActionButtons } from "./navbar/NavbarActionButtons";
import { ProfileDrawer } from "./navbar/ProfileDrawer";
import { useProfileDrawer } from "@/contexts/ProfileDrawerContext";
import { useAuth } from "@/contexts/AuthContext";

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, isLoading } = useProfile();
  const { user, isAuthenticated } = useAuth();
  const { openProfileDrawer } = useProfileDrawer();
  const [gibletChatOpen, setGibletChatOpen] = useState(false);
  const isMarketplace = location.pathname === "/marketplace";
  const isLandingPage = location.pathname === "/platform-select";
  const isIndex = location.pathname === "/";
  const isIntroPage = location.pathname === "/intro";

  const handleBackClick = () => {
    // Check if we can go back in history
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      // Fallback to home if no history
      navigate("/");
    }
  };

  // Simplified navbar for intro page
  if (isIntroPage) {
    return (
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <img 
                src="/lovable-uploads/cff3231d-8a8c-4f5b-9340-bc437fa8da36.png" 
                alt="iSPAI" 
                className="h-8"
              />
            </div>
            <div className="flex items-center">
              <h1 className="text-lg font-medium">Integrated Special Purpose AI (iSPAI)</h1>
            </div>
          </div>
        </div>
      </nav>
    );
  }

  // Standard navbar for other pages
  return (
    <>
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center gap-3">
              <button 
                onClick={handleBackClick}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Go back"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <button 
                onClick={() => navigate(isAuthenticated ? '/' : '/auth')}
                className="flex items-center"
              >
                <img 
                  src="/lovable-uploads/cff3231d-8a8c-4f5b-9340-bc437fa8da36.png" 
                  alt="iSPAI" 
                  className="h-8"
                />
              </button>
              {/* Profile Avatar - moved to left side */}
              <Tooltip>
                <TooltipTrigger asChild>
                  {isLoading ? (
                    <Avatar className="h-8 w-8 cursor-pointer">
                      <AvatarFallback>...</AvatarFallback>
                    </Avatar>
                  ) : profile?.username ? (
                    <button 
                      onClick={() => user?.id && openProfileDrawer(user.id)}
                      className="text-black hover:text-black/80 transition-colors"
                      aria-label="Open Profile Menu"
                    >
                      <Avatar className="h-8 w-8 transform transition-transform duration-200 hover:scale-110">
                        <AvatarImage src={profile.avatar_url || undefined} alt={`mySPAI_${profile.display_name}`} />
                        <AvatarFallback>
                          {profile.display_name?.split(' ').map(n => n[0]).join('') || 'U'}
                        </AvatarFallback>
                      </Avatar>
                    </button>
                  ) : (
                    <Link to="/create-myspai-profile" className="text-black hover:text-black/80 transition-colors" aria-label="Create Profile">
                      <Avatar className="h-8 w-8 transform transition-transform duration-200 hover:scale-110">
                        <AvatarFallback>+</AvatarFallback>
                      </Avatar>
                    </Link>
                  )}
                </TooltipTrigger>
                <TooltipContent>{profile?.username ? `mySPAI_${profile.display_name || 'User'}` : 'Create Profile'}</TooltipContent>
              </Tooltip>
            </div>
            
            <div className="flex items-center space-x-2">
              <NavbarActionButtons />
            </div>
            
            <div className="flex items-center space-x-4">
              <FeedbackButton />
              <NotificationBell />
              <GibletChatButton onClick={() => setGibletChatOpen(true)} />
            </div>
          </div>
        </div>
      </nav>
      
      <GibletChatDialogEnhanced
        open={gibletChatOpen}
        onOpenChange={setGibletChatOpen}
      />
      
      <ProfileDrawer />
    </>
  );
};

export default Navbar;
